import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { useCart } from '../../utils/contexts/cart-context';
import { useRestaurant } from '../../utils/contexts/restaurant-context';
import { useStickySession } from '../../logic/useStickyState';
import formats from '../../utils/formats';
import forms from '../../utils/forms';
import OrderTimetablesPopover from './OrderTimetablesPopover';
import TimetablesOrder from './TimetablesOrder';
import clsx from 'clsx';
import TakeawayCheckoutValue from './TakeawayCheckoutValue';

// ---UTILITY FUNCTIONS---
const DAY_MAP = { 'SUN': 0, 'MON': 1, 'TUE': 2, 'WED': 3, 'THU': 4, 'FRI': 5, 'SAT': 6 };

const getDayNumber = (dayString) => DAY_MAP[dayString];

const parseTimeString = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return { hours, minutes };
};

const createTimeFromString = (timeStr) => {
    const { hours, minutes } = parseTimeString(timeStr);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
};

const getMaxBookingDate = () => {
    const today = new Date();
    const twoWeeksLater = new Date(today);
    twoWeeksLater.setDate(today.getDate() + 14);

    const dayOfWeek = twoWeeksLater.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    const maxDate = new Date(twoWeeksLater);
    maxDate.setDate(twoWeeksLater.getDate() + daysUntilSunday);

    const year = maxDate.getFullYear();
    const month = (maxDate.getMonth() + 1).toString().padStart(2, '0');
    const day = maxDate.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}T23:59`;
};

const getMinBookingDateTime = (restaurant) => {
    const now = new Date();
    const minTime = formats.shiftTime(now, restaurant.orders.takeaway.medium_time);

    const isDayOpen = (date) => {
        const dayOfWeek = date.getDay();
        return restaurant.orders.takeaway.hours.some(schedule =>
            getDayNumber(schedule.day) === dayOfWeek
        );
    };

    const getFirstOpenTime = (date) => {
        const dayOfWeek = date.getDay();
        const daySchedules = restaurant.orders.takeaway.hours.filter(schedule =>
            getDayNumber(schedule.day) === dayOfWeek
        );

        if (daySchedules.length === 0) { return null; }

        const earliestSchedule = daySchedules.reduce((earliest, current) =>
            current.open < earliest.open ? current : earliest
        );

        return earliestSchedule.open;
    };

    let candidateDateTime = new Date();
    candidateDateTime.setHours(minTime.hours, minTime.minutes, 0, 0);

    if (candidateDateTime <= now) {
        candidateDateTime.setDate(candidateDateTime.getDate() + 1);
        candidateDateTime.setHours(minTime.hours, minTime.minutes, 0, 0);
    }

    let attempts = 0;
    while (attempts < 14) {
        if (isDayOpen(candidateDateTime)) {
            const firstOpenTime = getFirstOpenTime(candidateDateTime);
            if (firstOpenTime) {
                if (candidateDateTime.toDateString() === now.toDateString() &&
                    `${minTime.hours.toString().padStart(2, '0')}:${minTime.minutes.toString().padStart(2, '0')}` >= firstOpenTime) {
                    candidateDateTime.setHours(minTime.hours, minTime.minutes, 0, 0);
                } else {
                    const [hours, minutes] = firstOpenTime.split(':');
                    candidateDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                }
                break;
            }
        }

        candidateDateTime.setDate(candidateDateTime.getDate() + 1);
        attempts++;
    }

    const year = candidateDateTime.getFullYear();
    const month = (candidateDateTime.getMonth() + 1).toString().padStart(2, '0');
    const day = candidateDateTime.getDate().toString().padStart(2, '0');
    const hours = candidateDateTime.getHours().toString().padStart(2, '0');
    const minutes = candidateDateTime.getMinutes().toString().padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const isTimeValidForDay = (restaurant, dateTime) => {
    const selectedDate = new Date(dateTime);
    const dayOfWeek = selectedDate.getDay();
    const selectedTime = selectedDate.toTimeString().slice(0, 5);

    const daySchedules = restaurant.orders.takeaway.hours.filter(schedule =>
        getDayNumber(schedule.day) === dayOfWeek
    );

    if (daySchedules.length === 0) { return false; }

    return daySchedules.some(schedule =>
        selectedTime >= schedule.open && selectedTime <= schedule.close
    );
};

const isTimeInRange = (timeValue, timeRanges) => {
    if (!timeValue || timeRanges.length === 0) { return false; }

    const userTime = createTimeFromString(timeValue);

    return timeRanges.some(({ open, close }) => {
        const openTime = createTimeFromString(open);
        const closeTime = createTimeFromString(close);
        return userTime >= openTime && userTime <= closeTime;
    });
};

export default function TakeawayForm() {
    const [isDisabled, setIsDisabled] = React.useState(false);
    const [payload, setPayload] = React.useState({
        name: "",
        surname: "",
        time: "",
        datetime: "",
        phone: ""
    });
    const [correctTime, setCorrectTime] = React.useState(false);
    const [coupon] = useStickySession({}, "coupon");

    const { number, products, quantity, setTable, total, payed, setCustomer, setType, order_note, addOrderNote } = useCart();
    const history = useHistory();
    const { type, id } = useParams();
    const { restaurant } = useRestaurant();
    const { t } = useTranslation();

    const hasDateTime = restaurant.token === "DE2jHMAae";

    const { hours, minutes } = React.useMemo(() =>
        formats.shiftTime(new Date(), restaurant.orders.takeaway.medium_time),
        [restaurant.orders.takeaway.medium_time]
    );

    const minOrderTime = `${hours}:${minutes}`;

    const todaySchedules = React.useMemo(() => {
        if (!restaurant.orders.takeaway.active) { return []; }

        const today = new Date().getDay();
        return restaurant.orders.takeaway.hours
            .filter(schedule => getDayNumber(schedule.day) === today)
            .map(({ open, close }) => ({ open, close }));
    }, [restaurant.orders.takeaway.active, restaurant.orders.takeaway.hours]);

    const mediumTime = React.useMemo(() => {
        const time = Number(restaurant.orders.takeaway.medium_time);
        return time !== 0 ? time : null;
    }, [restaurant.orders.takeaway.medium_time]);

    React.useEffect(() => {
        const serviceUnavailable = !restaurant.orders.takeaway.active || todaySchedules.length === 0;
        setIsDisabled(serviceUnavailable);

        setCorrectTime(restaurant.has_pk_sms || !serviceUnavailable);
    }, [restaurant.orders.takeaway.active, todaySchedules.length, restaurant.has_pk_sms]);

    const handleInputChange = React.useCallback((e) => {
        const { name, value } = e.target;

        setPayload(prevState => {
            const newState = { ...prevState, [name]: value };

            if (name === "datetime" && value) {
                newState.time = new Date(value).toLocaleTimeString('it-IT', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                });
            }

            return newState;
        });
    }, []);

    const validateTime = React.useCallback((e) => {
        const { value } = e.target;

        if (hasDateTime) {
            const isValid = value ? isTimeValidForDay(restaurant, value) : false;
            setCorrectTime(isValid);
            return isValid;
        } else {
            const isValid = isTimeInRange(value, todaySchedules);
            setCorrectTime(isValid);
            return isValid;
        }
    }, [hasDateTime, restaurant, todaySchedules]);

    const onCheckoutHandler = React.useCallback(() => {
        setTable(-1);
        setCustomer(payload);
        setType("takeaway");
        history.push(`/${type}/q/${id}/orders/payments`);
    }, [payload, setTable, setCustomer, setType, history, type, id]);

    const onClickHandler = React.useCallback(() => {
        let finalPrice = total;

        if (coupon) {
            if (String(coupon.restaurantAccepted) === String(restaurant.id)) {
                finalPrice = formats.formatPercentageToPrice(coupon.discount, total);
            }
        }

        finalPrice = formats.roundPrice(finalPrice + restaurant.orders.takeaway.fee);

        const amount = formats.toCents(finalPrice);
        const commissions = formats.toCents(finalPrice * 0.02);

        const storage = {
            restaurant,
            order: {
                amount,
                commissions,
                customers: "1",
                customer: restaurant.has_pk_sms ? {
                    ...payload,
                    time: new Date().toLocaleTimeString('it-IT', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    })
                } : payload,
                number,
                payed,
                products,
                quantity,
                timestamp: Date.now(),
                table: "-1",
                total: finalPrice,
                type: "takeaway",
                note: order_note,
            },
            transaction: {
                id: forms.generateRandomID("cash_"),
                provider: "cash",
            }
        };

        window.localStorage.setItem("__mycontactlessmenu_payload__", JSON.stringify(storage));

        if (type === "r") {
            history.push(`/${type}/q/${id}/orders/success/?provider=cash`);
        } else {
            if (restaurant.has_whatsapp) {
                history.push(`/${type}/q/${id}/orders/whatsapp`);
            } else {
                history.push(`/${type}/q/${id}/orders/awaitsms/?provider=cash`);
            }
        }
    }, [payload, total, restaurant, number, payed, products, quantity, order_note, type, id, history, coupon]);

    const handleSubmit = React.useCallback((e) => {
        e.preventDefault();
        const submitType = e.nativeEvent.submitter?.id;

        if (!correctTime) { return false; }

        if (submitType === "cash") {
            onClickHandler();
        } else if (submitType === "cards") {
            onCheckoutHandler();
        }

        return false;
    }, [correctTime, onCheckoutHandler, onClickHandler]);

    if (isDisabled) {
        return (
            <React.Fragment>
                <p className="text-center p-10">
                    {t("takeawayform.p_1")}
                </p>
                <TimetablesOrder orders={restaurant.orders.takeaway.hours}></TimetablesOrder>
            </React.Fragment>
        );
    }

    return (
        <form id="takeaway-form" onSubmit={handleSubmit}>
            <p className="p-3 text-sm">
                {t("takeawayform.p_2")}
            </p>

            <div className="pt-3 p-4">
                <div className="flex">
                    <div className="w-6 ml-3 mr-6">
                        <svg xmlns="http://www.w3.org/2000/svg" className="fill-current text-mycia-red" viewBox="0 0 690.59 690.9"><g data-name="Livello 2"><path d="M264.52 50.76c-45.26 5.35-65.36 33.43-66.71 75.47-.67 15.53-.71 33.62-.68 49.86.55 9.54-1.38 17.68 1.35 26.95-33.23 23.41-8.22 91.1 22.91 106.47C232.82 337 245.1 369.22 268.56 389v52.56C138.88 458.59-8.42 547.19.38 690.9l690-1.35c1.46-34.17-4.39-67.12-21.57-98.38-47.51-85.87-145.19-133.7-242.58-148.24V385c21.12-17.78 32.46-48.21 43.13-72.78 22.24-13.64 33.69-37.21 36.39-63.34 1.49-14.73-.16-36-12.13-44.47-.65-8.11 1.46-15.34.67-22.91-.92-7.19-2-16.88-1.35-24.93 4.67-51.24 33.95-60.64-6.74-110.51C455.32 11.36 405.73-.33 358.85.89c-14.4-3.05-33.67 2.51-44.47 4.72-22.78 5.47-37.14 20.88-45.82 41.11M305 65.58c10.2-15.64 21.13-19.83 35.62-23.47 4.95-1.24 11.05-2.46 16.15-2.31 11.73.34 18.34.33 31.11 2.87 22 3.93 43.37 8 60.64 22.91 8.46 7.57 16.45 17.38 18.2 29-.43 3.36-2.85 6.28-2.7 10.11-13.19 23.55-12 53.64-10.78 80.86.36 13.35-3.78 38.77-5.39 52.55h18.86c-.32 7.16-1 12-4 17.52-1.42 8.41-6.6 13.53-9.43 20.22l-14.83-1.35c-6.41 24.36-23.57 73.2-45.82 84.9l-8.08 8.09v109.04c-51.34 44.24-25.68 47.41-75.56.11h.01V368.81c-6.48-6.54-14.78-8.72-20.22-17.52-8.33-12.68-16.72-22.86-22.91-38.41-5.16-12.23-9.87-24.37-12.13-37.06l-12.12-1.35c-6.34-4.75-11.27-10.3-13.48-18.87-3.17-5.22-3.22-7.94-5.39-14.82l20.56 2c-4.39-26.94-6.75-54.51-5.74-81.55 1.56-23.72-4.56-52.82 14.15-64.69a109.29 109.29 0 0116.85-5.39 19 19 0 0014.15-2.7M43.5 650.47C61.86 551.44 171.89 501.6 264 483.21c110.46 102.51 53.13 102.71 163.4.27 92.29 15.75 198.41 69.75 219.82 165.64z" data-name="Livello 1" /></g></svg>
                    </div>
                    <p>{t("takeawayform.p_3")}</p>
                </div>

                <input type="text" name="name" className="w-full mt-2 py-3 px-4 rounded-lg bg-grey-200" placeholder={t("takeawayform.input_1.placeholder_1")} onChange={handleInputChange} required />

                <input type="text" name="surname" className="w-full mt-2 py-3 px-4 rounded-lg bg-grey-200" placeholder={t("takeawayform.input_1.placeholder_2")} onChange={handleInputChange} required />

                {!restaurant.has_pk_sms && (
                    <div className="flex items-center py-6">
                        <div className="w-6 ml-3 mr-6">
                            <svg className="fill-current text-mycia-red" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 605.29 691.35"><g data-name="Livello 2"><path d="M570.86 251a192.64 192.64 0 0115.06 31.55A315.8 315.8 0 01596.68 317a285.24 285.24 0 016.46 35.14 308 308 0 012.15 36.57 289.14 289.14 0 01-5 53.79 296.85 296.85 0 01-14.35 50.92 354.42 354.42 0 01-22.23 47.33A265.39 265.39 0 01535 582.34l54.5 74.58-34.42 25.82-51.64-68.85a284.58 284.58 0 01-43 33 304.58 304.58 0 01-48.05 24.39 273.69 273.69 0 01-53.07 15.06 322.21 322.21 0 01-113.31 0 273.29 273.29 0 01-53.07-15.06 302.86 302.86 0 01-48-24.39 282.6 282.6 0 01-43-33L50.2 682.74l-34.42-25.82 54.5-74.58a264 264 0 01-28.68-41.6 352 352 0 01-22.24-47.33A294.14 294.14 0 015 442.49a287.45 287.45 0 01-5-53.79 308 308 0 012.15-36.57A285.24 285.24 0 018.61 317a315.73 315.73 0 0110.75-34.43A194.29 194.29 0 0134.42 251a130.54 130.54 0 01-15.06-17.93A100.7 100.7 0 018.61 213a118.59 118.59 0 01-6.46-22.23 124.58 124.58 0 01-2.15-23 114 114 0 019.32-45.9 117.24 117.24 0 0162.4-62.4 114.36 114.36 0 0145.9-9.32 110 110 0 0128 3.59 120.81 120.81 0 0125.82 10 122.81 122.81 0 0122.95 15.78 134.45 134.45 0 0119.37 20.79 125.13 125.13 0 0115.77-5q8.61-2.14 17.21-3.58t17.22-2.15q8.59-.72 17.21-2.16V43h-43V0h129.02v43h-43v44.49q8.61 1.45 17.21 2.16t17.21 2.15q8.61 1.44 17.21 3.58a125.82 125.82 0 0115.78 5 134 134 0 0119.37-20.79 122.16 122.16 0 0122.95-15.78 119.92 119.92 0 0125.81-10 109.91 109.91 0 0128-3.59 114.46 114.46 0 0145.9 9.32A117.27 117.27 0 01596 121.92a114.13 114.13 0 019.33 45.9 124.58 124.58 0 01-2.15 23 118.59 118.59 0 01-6.5 22.18 100.74 100.74 0 01-10.76 20.08A129.13 129.13 0 01570.86 251zM43 167.82A56 56 0 0044.46 180q1.42 6.45 2.87 11.47a59.57 59.57 0 004.31 10.76 90.15 90.15 0 005.73 10 297 297 0 0124.39-29.4A346.48 346.48 0 01109 157.06a272.47 272.47 0 0130.12-22.23 371.11 371.11 0 0133-18.65q-5.75-4.31-12.19-8.61T147 99.69a47.21 47.21 0 00-13.63-5 81.1 81.1 0 00-15.77-1.44 71.66 71.66 0 00-28 5.74 75.81 75.81 0 00-24.39 16.49 78.25 78.25 0 00-16.49 23.67A69.84 69.84 0 0043 167.82zm259.61 480.5a246.85 246.85 0 00100.41-20.8 267.36 267.36 0 0082.47-55.94 270.14 270.14 0 0055.94-81.76q20.77-46.59 20.8-101.12a247.17 247.17 0 00-20.8-100.4 264.68 264.68 0 00-138.38-138.41 252.74 252.74 0 00-200.81 0A264.68 264.68 0 0063.83 288.3 246.84 246.84 0 0043 388.7q0 54.53 20.8 101.12a266.7 266.7 0 00138.41 137.7 246.77 246.77 0 00100.43 20.8zm21.55-259.62V259.61h-43v172.12h216.55v-43zM487.67 93.23a81.13 81.13 0 00-15.78 1.44 47 47 0 00-13.62 5q-6.45 3.59-12.91 7.88a79.83 79.83 0 00-12.19 10.05 219.55 219.55 0 0133 17.21 276.29 276.29 0 0130.12 22.23 351.82 351.82 0 0127.25 25.82 298.35 298.35 0 0124.38 29.4 96.9 96.9 0 005.74-10 62.56 62.56 0 004.3-10.76q1.42-5 2.87-11.47a56.63 56.63 0 001.44-12.19 70.17 70.17 0 00-5.74-28.69A78.6 78.6 0 00540 115.46 76.13 76.13 0 00515.64 99a71.8 71.8 0 00-27.97-5.77z" data-name="Livello 1" /></g></svg>
                        </div>
                        <p className="pr-6">{t("takeawayform.p_4")}</p>

                        {hasDateTime ? (
                            <input type="datetime-local" name="datetime" min={getMinBookingDateTime(restaurant)} max={getMaxBookingDate()} className="mt-2 py-3 px-4 rounded-lg bg-grey-200" onBlur={validateTime} onChange={handleInputChange} required />
                        ) : (
                            <input type="time" name="time" min={minOrderTime} className="mt-2 py-3 px-4 rounded-lg bg-grey-200" placeholder="hh:mm" onBlur={validateTime} onChange={handleInputChange} required />
                        )}

                        <OrderTimetablesPopover orders={restaurant.orders.takeaway.hours}></OrderTimetablesPopover>
                    </div>
                )}

                {!restaurant.has_pk_sms && !correctTime && (
                    <p className="pt-3 text-red-600">
                        {t("takeawayform.p_5")}
                    </p>)}

                {!restaurant.has_pk_sms && mediumTime && (
                    <p className="px-3 pb-3 text-sm">
                        {t("takeawayform.p_6", { mediumTime: mediumTime })}
                    </p>)}

                <div className={clsx(["flex", restaurant.has_pk_sms && "pt-3"])}>
                    <div className="w-6 ml-3 mr-6">
                        <svg className="fill-current text-mycia-red" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 691.9 691.76"><g data-name="Livello 2"><path d="M691.89 545.9c.37 16.34-8.44 53.44-15.22 68.26-24 47-120.77 87.72-177.25 75.36-32.41-4-72.7-21.73-102.63-32.16C240.17 599.9 75.8 430.82 25.58 270.94c-11.49-30.1-18.3-48.67-23.32-78.56C-10.16 136 30.53 38.92 77.63 15.12 96.82 7.77 135.91-4 156.19 1.37q8.85 2.94 26 37.32c18 30.91 34.44 66.16 55.73 96.24 7.52 11.6 13.62 19.25 14 31.42-6.49 31.56-53.8 54-74.87 77.58-13.37 14.2-16.89 19.1-11.55 33.64 26.08 54.27 63 107.39 102.13 146.56 36.37 37.56 86.62 70.07 136.51 98 4.34 2.14 15.94 6.94 21.11 6.63 34.22-6.46 63.63-84 100.17-88.87 13.91-.58 33.39 15.76 43.7 22.59 24.56 14.92 59.43 33.15 84 47.13 29.04 16.68 38.14 16.83 38.77 36.29z" data-name="Livello 1" /></g></svg>
                    </div>
                    <p>{t("takeawayform.p_7")}</p>
                </div>

                <div className="flex">
                    <input value="+39" className="float-left w-16 mr-6 mt-2 py-3 px-4 rounded-lg bg-grey-200" disabled />
                    <input type="tel" name="phone" pattern="^((3[0-9][0-9]))(\d{7})$" className="float-left mt-2 py-3 px-4 rounded-lg bg-grey-200" placeholder="0000000000" onChange={handleInputChange} required />
                </div>

                <div className="flex pt-3">
                    <div className="w-6 ml-3 mr-6">
                        <svg xmlns="http://www.w3.org/2000/svg" className="fill-current text-mycia-red" viewBox="0 0 83.99 89.21"><g data-name="Livello 2"><path d="M56 0l27.81 27.82L84 89H18.43l.18-5.22h60l.17-52.34H52.34l.17-26.25H15.82l.18 5.24h2.26a7.84 7.84 0 013.13.61 9.15 9.15 0 012.52 1.65 6.26 6.26 0 011.65 2.53 13.7 13.7 0 01.7 3l-.18 44.86-12.69 26.13L0 63.3l.17-45a5.57 5.57 0 01.53-3 8.92 8.92 0 011.73-2.53A7.81 7.81 0 015 11a7.6 7.6 0 012.87-.61h2.95L10.61 0zM21 18.26a6.88 6.88 0 01-.26-1 1.37 1.37 0 00-.52-.87 3.39 3.39 0 00-.87-.53 3.24 3.24 0 00-1.13-.17H8a2.51 2.51 0 00-1 .17 4.18 4.18 0 00-.79.53 1.85 1.85 0 00-.61.87l-.34 1 .17 7.82h15.44zm-5.7 55.12L21 61.9l-.17-30.6h-5.01L15.65 63h-5l-.22-31.7h-5l-.21 30.6 5.91 11.48zM57.73 9l-.17 17.22H75L57.56 9z" data-name="Livello 1" /></g></svg>
                    </div>
                    <p>{t("takeawayform.p_8")}</p>
                </div>

                <div>
                    <input className="w-full mt-2 py-3 px-4 rounded-lg bg-grey-200" placeholder={t("takeawayform.input_2.placeholder_1")} name="notes" value={order_note} onChange={(e) => addOrderNote(e.target.value)} />
                </div>
            </div>

            <TakeawayCheckoutValue restaurant={restaurant} total={total} coupon={coupon}></TakeawayCheckoutValue>

            <div className="w-full flex flex-col justify-center px-4 py-2 bg-white">
                <div className="text-xs text-center py-4">
                    <input type="checkbox" id="terms" name="terms" required />
                    <label className="pl-2">
                        {t("formtable.lable_1.span_1")}
                        <a className="underline" href="https://www.cartaidentitalimentare.com/privacy-esercizi-commerciali/" target="_blank" rel="noreferrer">
                            {t("formtable.lable_1.a_1")}
                        </a>
                        {t("formtable.lable_1.span_2")}
                    </label>
                </div>
                {formats.shouldDisplayPaymentsV2(restaurant, "takeaway") && <button type="submit" id="cards" className="block mt-4 py-2 rounded-full bg-mycia-green">{t("tablespage.button_1")}</button>}
                {formats.shouldDisplayPaymentsButtonV2(restaurant, "cash", "takeaway") && (<button type="submit" id="cash" className="block mt-4 py-2 rounded-full bg-mycia-green">{t("takeawayform.button_1")}</button>)}
            </div>
        </form>
    );
}
